const DB_NAME = 'pfi_dashboard_upload_cache';
const DB_VERSION = 1;
const SESSION_STORE = 'upload_sessions';
const FILE_STORE = 'upload_files';
const ACTIVE_SESSION_KEY = 'pfi_active_upload_session_id';

const memorySessions = new Map();
const memoryFiles = new Map();

const hasIndexedDB = typeof indexedDB !== 'undefined' && !!indexedDB;

const now = () => Date.now();

const randomId = () => {
  if (typeof crypto !== 'undefined' && crypto?.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const fileKey = (sessionId, fileId) => `${sessionId}:${fileId}`;

const openDb = () => new Promise((resolve, reject) => {
  if (!hasIndexedDB) {
    resolve(null);
    return;
  }

  const request = indexedDB.open(DB_NAME, DB_VERSION);
  request.onupgradeneeded = () => {
    const db = request.result;
    if (!db.objectStoreNames.contains(SESSION_STORE)) {
      db.createObjectStore(SESSION_STORE, { keyPath: 'id' });
    }
    if (!db.objectStoreNames.contains(FILE_STORE)) {
      db.createObjectStore(FILE_STORE, { keyPath: 'key' });
    }
  };
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error || new Error('Failed to open upload cache database'));
});

const requestToPromise = (request) => new Promise((resolve, reject) => {
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error || new Error('IndexedDB request failed'));
});

const normalizeRelativePath = (file) => {
  if (!file) return '';
  if (typeof file.webkitRelativePath === 'string' && file.webkitRelativePath) {
    return file.webkitRelativePath;
  }
  return String(file.name || '');
};

const naturalUploadKey = (value) => String(value || '')
  .toLowerCase()
  .split(/(\d+)/)
  .map((part) => (/^\d+$/.test(part) ? Number(part) : part));

export const sortUploadFilesByName = (files = []) => Array.from(files || [])
  .filter(Boolean)
  .sort((a, b) => {
    const aKey = naturalUploadKey(normalizeRelativePath(a));
    const bKey = naturalUploadKey(normalizeRelativePath(b));
    const len = Math.max(aKey.length, bKey.length);
    for (let i = 0; i < len; i += 1) {
      if (aKey[i] == null) return -1;
      if (bKey[i] == null) return 1;
      if (aKey[i] === bKey[i]) continue;
      if (typeof aKey[i] === 'number' && typeof bKey[i] === 'number') {
        return aKey[i] - bKey[i];
      }
      return String(aKey[i]).localeCompare(String(bKey[i]), undefined, { numeric: true, sensitivity: 'base' });
    }
    return 0;
  });

export const deriveUploadElevationName = (file, fallbackElevation = 'Unsorted') => {
  const relativePath = normalizeRelativePath(file);
  const pathParts = String(relativePath || '').split('/').filter(Boolean);

  if (pathParts.length > 1) {
    return pathParts[pathParts.length - 2] || fallbackElevation;
  }

  const baseName = String(file?.name || '').replace(/\.[^.]+$/, '');
  const elevationMatch = baseName.match(/(?:^|[\s_-])(E(?:levation)?[\s-]*\d+)(?:[\s_-]|$)/i);
  if (elevationMatch?.[1]) {
    return elevationMatch[1].replace(/\s+/g, '').toUpperCase();
  }

  return fallbackElevation;
};

export const deriveUploadLevelName = (file, fallbackLevel = '') => {
  const relativePath = normalizeRelativePath(file);
  const baseName = String(relativePath.split('/').pop() || file?.name || '').replace(/\.[^.]+$/, '');
  const patterns = [
    /\b(?:level|lvl|floor|flr|storey|story)\s*[-_ ]*\s*(\d+)\b/i,
    /\bl\s*[-_ ]*\s*(\d+)\b/i,
  ];
  for (const pattern of patterns) {
    const match = baseName.match(pattern);
    if (match?.[1]) {
      return `L${Number(match[1])}`;
    }
  }
  return fallbackLevel;
};

export const isUploadNetworkError = (error) => {
  if (!error) return false;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return true;
  }
  const message = String(error?.message || error || '').toLowerCase();
  return (
    error?.name === 'TypeError' && message.includes('failed to fetch')
  ) || message.includes('networkerror') || message.includes('load failed');
};

const makeSessionContext = ({
  type = 'folder',
  projectId = null,
  buildingId = null,
  imageType = 'drone',
  elevationName = null,
  projectName = '',
}) => ({
  type,
  projectId: projectId != null ? String(projectId) : null,
  buildingId: buildingId != null ? String(buildingId) : null,
  imageType: imageType || 'drone',
  elevationName: elevationName || '',
  projectName: projectName || '',
});

const matchesContext = (session, context = {}) => {
  const actual = makeSessionContext(session || {});
  const expected = makeSessionContext(context);
  const compare = (field) => {
    if (context[field] == null || context[field] === '') return true;
    return String(actual[field] ?? '') === String(expected[field] ?? '');
  };
  return ['type', 'projectId', 'buildingId', 'imageType', 'elevationName'].every(compare);
};

const cloneSession = (session) => {
  if (!session) return null;
  return {
    ...session,
    files: Array.isArray(session.files)
      ? session.files.map((file) => ({ ...file }))
      : [],
  };
};

const buildFileRecord = (sessionId, file, index, elevationName = null) => {
  const fileId = randomId();
  const relativePath = normalizeRelativePath(file);
  return {
    key: fileKey(sessionId, fileId),
    id: fileId,
    sessionId,
    name: file?.name || relativePath || `file-${index + 1}`,
    relativePath,
    size: file?.size || 0,
    type: file?.type || '',
    lastModified: file?.lastModified || now(),
    order: index,
    status: 'pending',
    progress: 0,
    error: '',
    elevationName: elevationName || '',
    estimatedElevation: deriveUploadElevationName(file, elevationName || 'Unsorted'),
    estimatedFloor: deriveUploadLevelName(file, `L${index + 1}`),
    createdAt: now(),
    updatedAt: now(),
    blob: file,
  };
};

const readSessionFallback = async (sessionId) => {
  const session = memorySessions.get(sessionId);
  if (!session) return null;
  const files = Array.isArray(session.fileIds)
    ? session.fileIds
        .map((fileId) => memoryFiles.get(fileKey(sessionId, fileId)))
        .filter(Boolean)
        .sort((a, b) => (a.order || 0) - (b.order || 0))
    : [];
  return cloneSession({ ...session, files });
};

const listSessionsFallback = async () => Array.from(memorySessions.values()).map((session) => ({
  ...cloneSession(session),
  files: [],
}));

const putSession = async (session) => {
  const next = { ...session, updatedAt: now() };
  const db = await openDb();
  if (!db) {
    memorySessions.set(next.id, cloneSession(next));
    return next;
  }
  const tx = db.transaction(SESSION_STORE, 'readwrite');
  tx.objectStore(SESSION_STORE).put(next);
  await new Promise((resolve, reject) => {
    tx.oncomplete = resolve;
    tx.onerror = () => reject(tx.error || new Error('Failed to persist upload session'));
    tx.onabort = () => reject(tx.error || new Error('Upload session transaction aborted'));
  });
  return next;
};

const putFileRecord = async (record) => {
  const next = { ...record, updatedAt: now() };
  const db = await openDb();
  if (!db) {
    memoryFiles.set(next.key, { ...next });
    return next;
  }
  const tx = db.transaction(FILE_STORE, 'readwrite');
  tx.objectStore(FILE_STORE).put(next);
  await new Promise((resolve, reject) => {
    tx.oncomplete = resolve;
    tx.onerror = () => reject(tx.error || new Error('Failed to persist upload file'));
    tx.onabort = () => reject(tx.error || new Error('Upload file transaction aborted'));
  });
  return next;
};

const deleteSessionFileRecord = async (sessionId, fileId) => {
  const db = await openDb();
  const key = fileKey(sessionId, fileId);
  if (!db) {
    memoryFiles.delete(key);
    return;
  }
  const tx = db.transaction(FILE_STORE, 'readwrite');
  tx.objectStore(FILE_STORE).delete(key);
  await new Promise((resolve, reject) => {
    tx.oncomplete = resolve;
    tx.onerror = () => reject(tx.error || new Error('Failed to delete upload file'));
    tx.onabort = () => reject(tx.error || new Error('Upload file delete transaction aborted'));
  });
};

const getSessionRecord = async (sessionId) => {
  const db = await openDb();
  if (!db) {
    return memorySessions.get(sessionId) || null;
  }
  const tx = db.transaction(SESSION_STORE, 'readonly');
  const request = tx.objectStore(SESSION_STORE).get(sessionId);
  return requestToPromise(request);
};

const getFileRecord = async (sessionId, fileId) => {
  const db = await openDb();
  const key = fileKey(sessionId, fileId);
  if (!db) {
    return memoryFiles.get(key) || null;
  }
  const tx = db.transaction(FILE_STORE, 'readonly');
  const request = tx.objectStore(FILE_STORE).get(key);
  return requestToPromise(request);
};

const getAllSessionRecords = async () => {
  const db = await openDb();
  if (!db) {
    return listSessionsFallback();
  }
  const tx = db.transaction(SESSION_STORE, 'readonly');
  const request = tx.objectStore(SESSION_STORE).getAll();
  return requestToPromise(request);
};

const getAllFileRecordsForSession = async (sessionId) => {
  const db = await openDb();
  if (!db) {
    return Array.from(memoryFiles.values())
      .filter((record) => record.sessionId === sessionId)
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  }
  const tx = db.transaction(FILE_STORE, 'readonly');
  const request = tx.objectStore(FILE_STORE).getAll();
  const all = await requestToPromise(request);
  return (Array.isArray(all) ? all : [])
    .filter((record) => record.sessionId === sessionId)
    .sort((a, b) => (a.order || 0) - (b.order || 0));
};

export const createUploadSession = async ({
  type = 'folder',
  projectId = null,
  buildingId = null,
  imageType = 'drone',
  elevationName = null,
  projectName = '',
  files = [],
}) => {
  const sessionId = randomId();
  const orderedFiles = sortUploadFilesByName(files);
  const fileRecords = orderedFiles.map((file, index) => buildFileRecord(sessionId, file, index, elevationName));
  const session = {
    id: sessionId,
    type,
    ...makeSessionContext({ type, projectId, buildingId, imageType, elevationName, projectName }),
    status: 'local',
    fileIds: fileRecords.map((record) => record.id),
    createdAt: now(),
    updatedAt: now(),
  };

  await putSession(session);
  for (const record of fileRecords) {
    await putFileRecord(record);
  }

  try {
    localStorage.setItem(ACTIVE_SESSION_KEY, sessionId);
  } catch {}

  return loadUploadSession(sessionId);
};

export const loadUploadSession = async (sessionId) => {
  if (!sessionId) return null;
  const session = await getSessionRecord(sessionId);
  if (!session) return null;
  const files = await getAllFileRecordsForSession(sessionId);
  return cloneSession({
    ...session,
    files: files.map(({ blob, ...record }) => ({ ...record })),
  });
};

export const getUploadSessionFileIds = async (sessionId) => {
  const session = await getSessionRecord(sessionId);
  return Array.isArray(session?.fileIds) ? [...session.fileIds] : [];
};

export const loadStoredUploadFile = async (sessionId, fileId) => {
  const record = await getFileRecord(sessionId, fileId);
  if (!record?.blob) return null;

  const blob = record.blob;
  const fileName = record.name || record.relativePath || `upload-${fileId}`;
  const file = new File([blob], fileName, {
    type: record.type || blob.type || 'application/octet-stream',
    lastModified: record.lastModified || now(),
  });

  if (record.relativePath) {
    Object.defineProperty(file, 'webkitRelativePath', {
      value: record.relativePath,
      configurable: true,
    });
  }

  return file;
};

export const updateUploadFile = async (sessionId, fileId, patch = {}) => {
  const current = await getFileRecord(sessionId, fileId);
  if (!current) return null;
  const next = {
    ...current,
    ...patch,
    key: fileKey(sessionId, fileId),
    sessionId,
    updatedAt: now(),
  };
  await putFileRecord(next);
  return { ...next };
};

export const updateUploadSession = async (sessionId, patch = {}) => {
  const current = await getSessionRecord(sessionId);
  if (!current) return null;
  const next = {
    ...current,
    ...patch,
    updatedAt: now(),
  };
  await putSession(next);
  return cloneSession(next);
};

export const deleteUploadSession = async (sessionId) => {
  if (!sessionId) return;
  const session = await getSessionRecord(sessionId);
  const fileIds = Array.isArray(session?.fileIds) ? session.fileIds : [];

  const db = await openDb();
  if (!db) {
    fileIds.forEach((fileId) => memoryFiles.delete(fileKey(sessionId, fileId)));
    memorySessions.delete(sessionId);
  } else {
    const tx = db.transaction([SESSION_STORE, FILE_STORE], 'readwrite');
    const sessionStore = tx.objectStore(SESSION_STORE);
    const fileStore = tx.objectStore(FILE_STORE);
    sessionStore.delete(sessionId);
    fileIds.forEach((fileId) => fileStore.delete(fileKey(sessionId, fileId)));
    await new Promise((resolve, reject) => {
      tx.oncomplete = resolve;
      tx.onerror = () => reject(tx.error || new Error('Failed to delete upload session'));
      tx.onabort = () => reject(tx.error || new Error('Upload session delete transaction aborted'));
    });
  }

  try {
    const activeSessionId = localStorage.getItem(ACTIVE_SESSION_KEY);
    if (activeSessionId === sessionId) {
      localStorage.removeItem(ACTIVE_SESSION_KEY);
    }
  } catch {}
};

export const listUploadSessions = async (context = {}) => {
  const sessions = await getAllSessionRecords();
  const enriched = await Promise.all((Array.isArray(sessions) ? sessions : []).map(async (session) => {
    const files = await getAllFileRecordsForSession(session.id);
    return cloneSession({
      ...session,
      files: files.map(({ blob, ...record }) => ({ ...record })),
    });
  }));

  return enriched
    .filter((session) => matchesContext(session, context))
    .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
};

export const findLatestPendingUploadSession = async (context = {}) => {
  const sessions = await listUploadSessions(context);
  return sessions.find((session) => session.status !== 'completed' && session.status !== 'done' && session.status !== 'finished') || null;
};

export const summarizeUploadSession = (session) => {
  const files = Array.isArray(session?.files) ? session.files : [];
  const byElevation = {};
  let completed = 0;
  let failed = 0;
  let uploading = 0;
  let pending = 0;

  files.forEach((file) => {
    const elevation = file?.elevationName || file?.estimatedElevation || session?.elevationName || 'Unsorted';
    if (!byElevation[elevation]) {
      byElevation[elevation] = {
        total: 0,
        completed: 0,
        failed: 0,
        uploading: 0,
        pending: 0,
        percent: 0,
        status: 'pending',
      };
    }
    const bucket = byElevation[elevation];
    bucket.total += 1;

    if (file.status === 'completed') {
      completed += 1;
      bucket.completed += 1;
    } else if (file.status === 'failed') {
      failed += 1;
      bucket.failed += 1;
    } else if (file.status === 'uploading') {
      uploading += 1;
      bucket.uploading += 1;
    } else {
      pending += 1;
      bucket.pending += 1;
    }
  });

  Object.values(byElevation).forEach((bucket) => {
    bucket.percent = bucket.total > 0 ? Math.round((bucket.completed / bucket.total) * 100) : 0;
    if (bucket.completed === bucket.total && bucket.total > 0) {
      bucket.status = 'completed';
    } else if (bucket.uploading > 0) {
      bucket.status = 'uploading';
    } else if (bucket.failed > 0) {
      bucket.status = 'failed';
    } else {
      bucket.status = 'pending';
    }
  });

  const total = files.length;
  const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
  const resumable = pending > 0 || failed > 0 || uploading > 0;

  return {
    total,
    completed,
    failed,
    uploading,
    pending,
    percent,
    resumable,
    byElevation,
    status: session?.status || 'pending',
    id: session?.id || null,
    projectId: session?.projectId || null,
    buildingId: session?.buildingId || null,
    imageType: session?.imageType || 'drone',
    elevationName: session?.elevationName || '',
    projectName: session?.projectName || '',
    type: session?.type || 'folder',
    files,
  };
};

export const buildUploadImagesFromResult = (result, {
  fallbackElevation = 'Unsorted',
  projectName = 'Uploaded Project',
  elevationName = null,
  getImageUrl = null,
} = {}) => {
  const folderStructure = {};
  const images = [];

  const pushImage = (backendImage, elevationKey, index) => {
    if (!backendImage) return;
    const key = elevationKey || fallbackElevation || 'Unsorted';
    const imageObject = {
      id: backendImage.id,
      src: backendImage.id && typeof getImageUrl === 'function'
        ? getImageUrl(backendImage.id)
        : (backendImage.url || ''),
      elevation: key,
      sourceElevationName: backendImage.elevation_name || key,
      originalElevationName: backendImage.elevation_name || key,
      floor: backendImage.floor_name || `Floor ${index + 1}`,
      filename: backendImage.filename,
      url: backendImage.url,
      folderPath: key,
      folderName: key,
      projectName,
      uploadStatus: 'server',
      uploadSource: 'server',
    };
    images.push(imageObject);
    if (!folderStructure[key]) {
      folderStructure[key] = [];
    }
    folderStructure[key].push(imageObject);
  };

  if (result?.folder_structure && typeof result.folder_structure === 'object') {
    Object.entries(result.folder_structure).forEach(([elevationKey, items]) => {
      (Array.isArray(items) ? items : []).forEach((backendImage, index) => {
        pushImage(backendImage, elevationKey, index);
      });
    });
  } else if (Array.isArray(result?.images)) {
    result.images.forEach((backendImage, index) => {
      const key = backendImage?.elevation_name || elevationName || fallbackElevation;
      pushImage(backendImage, key, index);
    });
  }

  return { images, folderStructure };
};

export const buildLocalPreviewFromFiles = (files, {
  projectName = 'Uploaded Project',
  fallbackElevation = 'Unsorted',
  getObjectUrl = null,
  getServerImageUrl = null,
  fileMeta = [],
} = {}) => {
  const folderStructure = {};
  const images = [];
  const urls = [];

  (Array.from(files || []) || []).forEach((file, index) => {
    if (!file) return;
    const meta = fileMeta[index] || {};
    const key = deriveUploadElevationName(file, fallbackElevation || 'Unsorted');
    const status = meta.status || 'pending';
    const isServer = status === 'completed' || status === 'server' || Boolean(meta.serverImageId || meta.serverUrl);
    const objectUrl = !isServer
      ? (typeof getObjectUrl === 'function'
          ? getObjectUrl(file)
          : (typeof URL !== 'undefined' && URL.createObjectURL ? URL.createObjectURL(file) : ''))
      : '';
    if (objectUrl) urls.push(objectUrl);

    const imageObject = {
      id: meta.serverImageId || meta.id || file?.id || `${file?.name || 'file'}-${index}`,
      src: isServer
        ? (meta.serverUrl || (typeof getServerImageUrl === 'function' && meta.serverImageId ? getServerImageUrl(meta.serverImageId) : objectUrl || ''))
        : (objectUrl || ''),
      elevation: key,
      sourceElevationName: key,
      originalElevationName: key,
      floor: meta.floor_name || meta.floorName || meta.estimatedFloor || deriveUploadLevelName(file, `L${index + 1}`),
      filename: file?.name || `file-${index + 1}`,
      url: isServer ? (meta.serverUrl || '') : (objectUrl || ''),
      folderPath: key,
      folderName: key,
      projectName,
      uploadStatus: isServer ? 'server' : (status === 'uploading' ? 'uploading' : status === 'failed' ? 'failed' : 'local'),
      uploadSource: isServer ? 'server' : 'local',
      localPreview: !isServer,
    };

    images.push(imageObject);
    if (!folderStructure[key]) {
      folderStructure[key] = [];
    }
    folderStructure[key].push(imageObject);
  });

  return { images, folderStructure, urls };
};
